import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

/**
 * Review types for professional verification
 */
export type ReviewType = 'APPROVE' | 'REQUEST_MODIFICATION' | 'REJECT';

/**
 * Review request payload
 */
export interface ReviewRequest {
  reviewType: ReviewType;
  notes?: string;
  file?: File;
}

/**
 * Review response from backend
 */
export interface Review {
  id: string;
  registrationStepId: string;
  reviewType: ReviewType;
  notes: string | null;
  reviewedBy: string;
  reviewedAt: string;
  fileUrl: string | null;
}

/**
 * API response wrapper
 */
export interface ReviewsResponse {
  data: Review[];
  message?: string;
}

export interface ReviewSubmitResponse {
  data: Review;
  message?: string;
}

/**
 * Service for managing professional reviews (approve/modify/reject)
 */
@Injectable({
  providedIn: 'root'
})
export class ReviewService {
  private http = inject(HttpClient);
  private readonly API_BASE_URL = 'api/v1/professionals';

  /**
   * Submit a review for a professional in VERIFICATION step
   * 
   * @param professionalId Professional ID (e.g., "FD-00123")
   * @param request Review request with type, notes, and optional file
   * @returns Observable with review response
   */
  submitReview(professionalId: string, request: ReviewRequest): Observable<ReviewSubmitResponse> {
    const formData = new FormData();
    formData.append('reviewType', request.reviewType);
    
    if (request.notes) {
      formData.append('notes', request.notes);
    }
    
    if (request.file) {
      formData.append('file', request.file, request.file.name);
    }

    // Don't set Content-Type header - browser will set it automatically with boundary
    return this.http.post<ReviewSubmitResponse>(
      `${environment.professionalsApiUrl}/${this.API_BASE_URL}/${professionalId}/review`,
      formData
    );
  }

  /**
   * Get review history for a professional
   * 
   * @param professionalId Professional ID
   * @returns Observable with array of reviews
   */
  getReviewHistory(professionalId: string): Observable<ReviewsResponse> {
    return this.http.get<ReviewsResponse>(
      `${environment.professionalsApiUrl}/${this.API_BASE_URL}/${professionalId}/reviews`
    );
  }

  /**
   * Validate review request before submission
   * 
   * @param request Review request to validate
   * @returns Error message if invalid, null if valid
   */
  validateReviewRequest(request: ReviewRequest): string | null {
    // Notes are required for REQUEST_MODIFICATION and REJECT
    if ((request.reviewType === 'REQUEST_MODIFICATION' || request.reviewType === 'REJECT') 
        && (!request.notes || request.notes.trim().length === 0)) {
      return 'Le note sono obbligatorie per "Richiedi Modifica" e "Rifiuta"';
    }

    // Validate file if present
    if (request.file) {
      // Check file type (only PDF)
      if (request.file.type !== 'application/pdf') {
        return 'Solo file PDF sono accettati';
      }

      // Check file size (max 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      if (request.file.size > maxSize) {
        return 'Il file non può superare i 10MB';
      }
    }

    return null; // Valid
  }
}
